import { gql, useApolloClient } from "@apollo/client";
import { Intent } from "@blueprintjs/core";
import { Popover2InteractionKind } from "@blueprintjs/popover2";
import {
  DELETE_VCS_PACKAGE_HEX_VERSION_LINK,
  DOCS_LINKS,
  OrgRole,
  ProjectLanguage,
  RESTART_APP_SESSION,
  VcsConnectionType,
  VcsPackageHexVersionLinkId,
  VcsPackageId,
  isOrgRoleSuperset,
  typedObjectKeys,
} from "@hex/common";
import React, { useCallback, useState } from "react";
import styled, { css } from "styled-components";

import {
  HexMenu,
  HexMenuItem,
  HexNonIdealState,
  HexPopover,
  HexSpinner,
  HexTooltip,
} from "../../hex-components";
import { useCurrentUser } from "../../hooks/me/useCurrentUser";
import { useOnClickProps } from "../../hooks/useOnClickProps";
import { refreshProductVersionStatus } from "../../hooks/useProductVersionStatus";
import { useToggleState } from "../../hooks/useToggleState";
import { Routes } from "../../route/routes";
import { useAppSessionAOContext } from "../../util/appSessionAOContext";
import { useHexVersionAOContext } from "../../util/hexVersionAOContext";
import { useProjectContext } from "../../util/projectContext";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { DocsLink } from "../common/DocsLink";
import { MinimalTable } from "../common/MinimalTable";
import { FeatureGateToolTip } from "../feature-gate/FeatureGateToolTip";
import {
  ArrowTopRightIcon,
  DownloadIcon,
  GlobeIcon,
} from "../icons/CustomIcons";
import { PackageLinkMap } from "../sidebar/EnvironmentView";
import { AddActionButton, SidebarHeader } from "../sidebar/SidebarHeader";
import { SidebarDivider, SidebarGroup } from "../sidebar/SidebarWrapper";

import { GithubPackageImporter } from "./GithubPackageImporter";
import { renderVcsPackageTableCells } from "./OrgRepoList";
import { useGetVcsPackagesForProjectQuery } from "./ProjectGithubPackages.generated";
import { VcsPackageFragment } from "./queries.generated";

gql`
  query GetVcsPackagesForProject($vcsPackageIds: [VcsPackageId!]!) {
    getVcsPackagesById(vcsPackageIds: $vcsPackageIds) {
      ...VcsPackageFragment
    }
  }
`;

const tableColumns = [
  {
    id: "packages-1",
    name: "",
  },
  {
    id: "packages-2",
    name: "",
    css: css`
      flex: none;
    `,
  },
];

const emptyState = {
  title: "No packages yet",
  $minimal: true,
};

const PackageContainer = styled.div``;

export interface GithubPackagesProps {
  vcsPackageIds: PackageLinkMap;
  notGated: boolean;
}

export const GithubPackages: React.FunctionComponent<GithubPackagesProps> =
  React.memo(function GithubPackages({ notGated, vcsPackageIds }) {
    const [openMenuId, setOpenMenuId] = useState<string>();
    const openPackageMenu = useCallback(
      (id) => setOpenMenuId(id),
      [setOpenMenuId],
    );
    const closePackageMenu = useCallback(
      () => setOpenMenuId(undefined),
      [setOpenMenuId],
    );
    const { language, projectEditable } = useProjectContext();
    const { data, loading } = useGetVcsPackagesForProjectQuery({
      variables: {
        vcsPackageIds: typedObjectKeys(vcsPackageIds),
      },
    });
    const currentUser = useCurrentUser();
    const editorRoles: OrgRole[] = [OrgRole.ADMIN, OrgRole.EDITOR];
    const canEdit =
      (currentUser?.orgRole != null &&
        editorRoles.includes(currentUser.orgRole) &&
        projectEditable) ??
      false;

    const [
      packageImportDialogOpen,
      ,
      { setFalse: closePackageImportDialog, setTrue: openPackageImportDialog },
    ] = useToggleState(false);

    const orgAdminOnClickProps = useOnClickProps({
      target: "_blank",
      to: Routes.SETTINGS.getUrl({
        subView: "workspace-assets",
      }),
    });

    const askYourAdminClickProps = useOnClickProps({
      to: Routes.SETTINGS.getUrl({ subView: "users" }),
      target: "_blank",
    });

    const addAction = (
      <HexPopover
        content={
          <HexMenu>
            <HexMenuItem
              disabled={loading || !canEdit || !notGated}
              icon={<DownloadIcon />}
              text={
                notGated ? (
                  <HexTooltip
                    content={`Only members of ${
                      currentUser?.org.displayName ?? "this workspace"
                    } with edit access to this project can import GitHub packages`}
                    disabled={canEdit}
                  >
                    Import workspace package...
                  </HexTooltip>
                ) : (
                  <FeatureGateToolTip
                    content={<>{`enable GitHub packages`}</>}
                    css={`
                      margin-left: 3px;
                    `}
                  >
                    Import workspace package...
                  </FeatureGateToolTip>
                )
              }
              onClick={openPackageImportDialog}
            />
            <HexMenuItem
              disabled={currentUser?.orgRole !== OrgRole.ADMIN || !notGated}
              icon={<GlobeIcon />}
              labelElement={<ArrowTopRightIcon />}
              text={
                notGated ? (
                  <HexTooltip
                    content={
                      <>
                        {currentUser?.orgRole &&
                        isOrgRoleSuperset(
                          currentUser.orgRole,
                          OrgRole.MEMBER,
                        ) ? (
                          <a {...askYourAdminClickProps}>A workspace admin</a>
                        ) : (
                          "Only admins"
                        )}{" "}
                        of {currentUser?.org.displayName ?? "this workspace"}{" "}
                        can create GitHub packages.
                      </>
                    }
                    disabled={currentUser?.orgRole === OrgRole.ADMIN}
                    interactionKind={Popover2InteractionKind.HOVER}
                  >
                    Create workspace package
                  </HexTooltip>
                ) : (
                  <FeatureGateToolTip
                    content={<>{`enable GitHub packages`}</>}
                    css={`
                      margin-left: 3px;
                    `}
                  >
                    Create workspace package
                  </FeatureGateToolTip>
                )
              }
              {...orgAdminOnClickProps}
            />
          </HexMenu>
        }
        minimal={true}
        placement="bottom-end"
      >
        <AddActionButton />
      </HexPopover>
    );

    const [packageToDelete, setPackageToDelete] = useState<
      VcsPackageHexVersionLinkId | undefined
    >(undefined);

    const { dispatchAO: hexVersionAO } = useHexVersionAOContext();
    const { dispatchAO: appSessionAO } = useAppSessionAOContext();
    const client = useApolloClient();
    const unmapPackageCallback = useCallback(async () => {
      if (packageToDelete) {
        await hexVersionAO(
          DELETE_VCS_PACKAGE_HEX_VERSION_LINK.create(packageToDelete),
        )[1];
        await appSessionAO(RESTART_APP_SESSION.create())[1];
        refreshProductVersionStatus(client);
      }
      setPackageToDelete(undefined);
    }, [appSessionAO, client, hexVersionAO, packageToDelete]);

    const generateMenuItems = useCallback(
      (vcsPackageHexVersionLinkId: VcsPackageHexVersionLinkId) => {
        return [
          <HexMenuItem
            key="removeMenuItem"
            disabled={!projectEditable}
            text="Remove from project..."
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => setPackageToDelete(vcsPackageHexVersionLinkId)}
          />,
        ];
      },
      [projectEditable],
    );

    const renderPackageCallback = useCallback(
      (vcsPackage: VcsPackageFragment) => {
        const menuItems = generateMenuItems(vcsPackageIds[vcsPackage.id]);
        return renderVcsPackageTableCells({
          connectionType: VcsConnectionType.GITHUB,
          repo: vcsPackage,
          menuItems,
          isOpen: openMenuId == vcsPackage.id,
          openRepoMenu: openPackageMenu,
          closeRepoMenu: closePackageMenu,
        });
      },
      [
        closePackageMenu,
        generateMenuItems,
        openMenuId,
        openPackageMenu,
        vcsPackageIds,
      ],
    );
    return (
      <>
        <ConfirmationDialog
          confirmationButtonIntent={Intent.DANGER}
          confirmationText="Are you sure you want to remove this package? This will automatically trigger a kernel restart."
          isOpen={!!packageToDelete}
          title="Delete package"
          // eslint-disable-next-line react/jsx-no-bind
          onClose={() => setPackageToDelete(undefined)}
          onConfirm={unmapPackageCallback}
        />
        <SidebarGroup>
          <SidebarHeader
            description={
              <>
                Imported packages to use in{" "}
                {language == ProjectLanguage.PYTHON ? "Python" : "R"} cells.{" "}
                <DocsLink to={DOCS_LINKS.GitHubPackageUse}>
                  Learn more.
                </DocsLink>
              </>
            }
            title="GitHub packages"
            topActions={addAction}
          />
          <PackageContainer>
            {loading && notGated && <HexSpinner description="Loading..." />}
            {(!notGated || (data && data.getVcsPackagesById.length == 0)) && (
              <HexNonIdealState
                {...emptyState}
                css={`
                  margin: 0 15px;
                  width: auto;
                `}
              />
            )}
            {notGated && data && data.getVcsPackagesById.length > 0 && (
              <MinimalTable
                columns={tableColumns}
                data={data.getVcsPackagesById}
                dataToRows={renderPackageCallback}
                rowCSS={css`
                  align-items: flex-start;
                  padding-bottom: 10px;

                  &:last-child {
                    padding-bottom: 0;
                  }
                `}
                showHeader={false}
              />
            )}
          </PackageContainer>
          {packageImportDialogOpen && (
            <GithubPackageImporter
              closeDialog={closePackageImportDialog}
              dialogOpen={packageImportDialogOpen}
              importedPackages={
                new Set(Object.keys(vcsPackageIds) as VcsPackageId[])
              }
            />
          )}
        </SidebarGroup>
        <SidebarDivider />
      </>
    );
  });
